import type { ArticleType } from "@acme/types";
import { imageCdnFullWidthProps } from "@acme/ui/image-cdn";
import { calculateReadingTime } from "@acme/utils";
import { Link } from "@tanstack/react-router";
import { Image } from "@unpic/react";
import { categoryLabel } from "~/lib/constants/blog-categories";
import { plural } from "~/lib/plural";
import * as m from "~/paraglide/messages.js";

interface ArticleCardProps {
  article: ArticleType;
  /** The lead article runs wide, with its art beside the copy. */
  variant?: "feature" | "tile";
}

/**
 * The art panel.
 *
 * The design draws a composed artifact per article — a brief, a review, a table
 * of owners — which a record cannot carry, so the frame and its ground stay and
 * the article's own cover fills them. Without a cover it names the subject.
 */
function ArticleArt({ article }: Readonly<{ article: ArticleType }>) {
  if (!article.imageUrl) {
    return (
      <div className="editorial-art">
        <span className="art-fallback">{categoryLabel(article.category)}</span>
      </div>
    );
  }

  return (
    <div className="editorial-art">
      <Image
        alt=""
        layout="fullWidth"
        sizes="(max-width: 720px) 100vw, (max-width: 1100px) 50vw, 45vw"
        src={article.imageUrl}
        {...imageCdnFullWidthProps(article.imageUrl)}
      />
    </div>
  );
}

/** The subject and the reading time, in that order, above every title. */
export function ArticleMeta({ article }: Readonly<{ article: ArticleType }>) {
  const minutes = calculateReadingTime(article.content ?? "");

  return (
    <p className="entry-meta">
      <span className="entry-tag">{categoryLabel(article.category)}</span>
      <span>
        {plural(minutes, {
          one: m.blog_reading_one,
          other: m.blog_reading_other,
          zero: m.blog_reading_zero,
        })}
      </span>
    </p>
  );
}

/**
 * One article in the listing.
 *
 * The whole card is a single link rather than a card with a link inside it, so
 * a keyboard reaches each article once.
 */
export function ArticleCard({
  article,
  variant = "tile",
}: Readonly<ArticleCardProps>) {
  const isFeature = variant === "feature";

  return (
    <Link
      className={isFeature ? "post-feature" : "post-card"}
      params={{ articleId: article.slug }}
      to="/blog/$articleId"
    >
      <ArticleArt article={article} />

      <div className={isFeature ? "post-feature-copy" : "post-card-copy"}>
        <ArticleMeta article={article} />
        <h2>{article.title}</h2>
        {article.description ? <p>{article.description}</p> : null}
        <span className="read-link">
          {m.blog_read_link()} <span aria-hidden="true">↗</span>
        </span>
      </div>
    </Link>
  );
}
